// xtransport.ts ↔ the client's WebSocket transport: the XTS_* connection state
// machine of game/xtransport.cpp (XTransport over mpshared/xpump.cpp's XMsg pump)
// plus the Transport callback plumbing of game/comm.cpp. The wire codec is NOT
// re-ported — every frame goes through the golden-vector-validated
// cloudflare/protocol/ package (xmsg.ts framing, netmessage.ts game frames,
// gamemsg.ts inner structs, build.ts client builders).
//
// SHAPE NOTES (the two C++→TS seams, both anchored below):
//  1. The C++ socket is a blocking-ish base::Socket pumped by the thread's event
//     loop; WaitForStateChange (xtransport.cpp:193-220) spins a modal wait that
//     keeps dispatching socket events until SetState wakes it. Here the socket
//     is an injected `XConnector` (a WebSocket in production, a scripted socket
//     in tests) whose callbacks fire on the JS event loop, and
//     WaitForStateChange awaits a state-change promise with the same 30s
//     default timeout (kctWaitMax = 30*100 ticks). Public verbs (Open/Login/
//     Join*/Create*/Leave*) are therefore async — each is the C++ synchronous
//     call with the internal modal wait made awaitable; the send-then-wait
//     ordering inside each verb is preserved verbatim.
//  2. XPump::Dispatch (xpump.cpp:141-165) decodes one frame per event-loop pass;
//     here the pump dispatches every complete frame as bytes arrive (the JS
//     event loop IS the pass). Frame REASSEMBLY is identical: bytes accumulate
//     and frames split on the leading big-endian u16 size (XMsg::ExtractSize),
//     so TCP-style coalescing/splitting is handled.
//
// XMsgGameUpdateEmpty (0x55) is NOT a distinct callback: like the C++
// (messages.cpp:304-339 FromBuffer returns an XMsgGameNetMessage wrapping a
// zero-command ScUpdate), it synthesizes an ScUpdate inner and flows through
// OnGameNetMessage. CsUpdateResult likewise rides the optimized 0x56 frame on
// send (messages.cpp:205-245 ToBuffer special-cases it).

import { decode } from '../../../protocol/xmsg.ts';
import { decodeGame, encodeGame } from '../../../protocol/netmessage.ts';
import {
  decodeInner,
  encodeInner,
  decodeGameParams,
  encodeGameParams,
  type GameInner,
  type GameParams,
} from '../../../protocol/gamemsg.ts';
import { ByteReader, ByteWriter } from '../../../protocol/bytebuffer.ts';
import * as B from '../../../protocol/build.ts';
import { XMSG, NMID, CLIENT_ID, PROTOCOL_CURRENT } from '../../../protocol/constants.ts';

// ── result codes ──────────────────────────────────────────────────────────────
// game/ht.h:331-337
export const knTransportOpenResultSuccess = 0;
export const knTransportOpenResultFail = 1;
export const knTransportOpenResultNoNetwork = 2;
export const knTransportOpenResultCantConnect = 3;
export const knTransportOpenResultNotResponding = 4;
export const knTransportOpenResultProtocolMismatch = 5;
export const knTransportOpenResultServerFull = 6;
// mpshared/messages.h:25-27
export const knHandshakeResultSuccess = 0;
export const knHandshakeResultServerFull = 2;
// mpshared/messages.h:75-82
export const knLoginResultSuccess = 0;
export const knLoginResultAnonymousSuccess = 1;
export const knLoginResultFail = 2;
// mpshared/messages.h:95-96
export const knSignOutResultSuccess = 0;
export const knSignOutResultFail = 1;
// mpshared/messages.h:125-128
export const knLobbyJoinResultSuccess = 0;
export const knLobbyJoinResultFail = 1;
// mpshared/messages.h:13-16
export const knLobbyCreateRoomResultSuccess = 0;
export const knLobbyCreateRoomResultFail = 1;
// mpshared/messages.h:103-107
export const knRoomJoinResultSuccess = 0;
export const knRoomJoinResultFail = 1;
// mpshared/messages.h:35-40
export const knGameJoinResultSuccess = 0;
export const knGameJoinResultFail = 1;
// mpshared/messages.h:61-65
export const knRoomCreateGameResultSuccess = 0;
export const knRoomCreateGameResultFail = 1;

// mpht.h PackId — id + 16-byte hash (decoded out of ROOMCREATEGAMERESULT).
export interface PackId {
  id: number;
  hash: Uint8Array;
}

// mpshared/mpht.h PlayerName (char[kcbPlayerName]) records of ROOMGAMEPLAYERNAMES.
export const kcbPlayerName = 32;

// ── the callback interfaces (game/ht.h:413-452, game/comm.cpp) ───────────────
export interface ITransportCallback {
  OnStatusUpdate(pszStatus: string): void;
  OnConnectionClose(): void;
  OnShowMessage(message: string): void;
}
export interface ILobbyCallback {
  OnLurkerCount(count: number): void;
  OnAddRoom(name: string, roomid: number, priv: boolean, cPlayers: number, cGames: number): void;
  OnRemoveRoom(roomid: number): void;
  OnUpdateRoom(roomid: number, cPlayers: number, cGames: number): void;
  OnReceiveChat(player: string, chat: string): void;
}
export interface IRoomCallback {
  OnAddGame(
    player: string,
    gameid: number,
    params: GameParams,
    minplayers: number,
    maxplayers: number,
    title: string,
    ctotal: number,
  ): void;
  OnRemoveGame(gameid: number, ctotal: number): void;
  OnGameInProgress(gameid: number): void;
  OnGamePlayerNames(gameid: number, cnames: number, anames: string[]): void;
  OnAddPlayer(player: string): void;
  OnRemovePlayer(hint: number, player: string): void;
  OnReceiveChat(player: string, chat: string): void;
  OnStatusComplete(): void;
}
/** game/ht.h:414-418 — the C++ hands `NetMessage **ppnm`; here the decoded
 *  GameInner (protocol/gamemsg.ts) is the NetMessage. */
export interface IGameCallback {
  OnReceiveChat(player: string, chat: string): void;
  OnNetMessage(nm: GameInner): void;
  OnGameDisconnect(): void;
}

// ── the socket seam ───────────────────────────────────────────────────────────
/** What the transport needs from a socket (base::Socket through the
 *  SocketNotify seam, xtransport.h:81-84). Callbacks MUST fire asynchronously
 *  (event-loop turn) — the C++ only ever processes socket events inside its
 *  wait loops; a synchronous callback re-entering the state machine mid-`send`
 *  has no C++ counterpart. */
export interface XSocketNotify {
  OnConnectEvent(): void;
  /** Received bytes (any framing) — the pump reassembles XMsg frames. */
  OnBytes(bytes: Uint8Array): void;
  OnError(error: string): void;
  OnClose(): void;
}
export interface XSocket {
  send(bytes: Uint8Array): void;
  close(): void;
}
export type XConnector = (notify: XSocketNotify) => XSocket;

/** The production connector: a WebSocket speaking the 'binary' subprotocol
 *  (the same socket the DO accepts; test/harness/transport.ts uses the
 *  identical construction). Works on Node ≥21 (global WebSocket) and browsers. */
export function webSocketConnector(url: string): XConnector {
  return (notify) => {
    const ws = new WebSocket(url, 'binary');
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => notify.OnConnectEvent();
    ws.onmessage = (ev) => notify.OnBytes(new Uint8Array(ev.data as ArrayBuffer));
    ws.onerror = () => notify.OnError('ws error');
    ws.onclose = () => notify.OnClose();
    return {
      // TS6+ lib.dom: ws.send() wants ArrayBuffer-backed views; XSocket frames never use SharedArrayBuffer
      send: (bytes) => ws.send(bytes as Uint8Array<ArrayBuffer>),
      close: () => ws.close(),
    };
  };
}

// ── XPumpNotify (mpshared/xpump.h:30-90) ─────────────────────────────────────
export interface XPumpNotify {
  OnHandshakeResult(result: number, id: number): void;
  OnShowMessage(message: string, ipRedirect: number, disconnect: boolean): void;
  OnEcho(): void;
  OnLoginResult(result: number): void;
  OnSignOutResult(result: number): void;
  OnLobbyJoinResult(result: number): void;
  OnLobbyLeaveResult(result: number): void;
  OnLobbyCreateRoomResult(result: number, roomid: number): void;
  OnLobbyCanJoinRoomResult(result: number): void;
  OnLobbyLurkerCount(count: number): void;
  OnLobbyAddRoom(
    room: string,
    roomid: number,
    priv: boolean,
    cPlayers: number,
    cGames: number,
  ): void;
  OnLobbyRemoveRoom(roomid: number): void;
  OnLobbyUpdateRoom(roomid: number, cPlayers: number, cGames: number): void;
  OnLobbyReceiveChat(player: string, chat: string): void;
  OnRoomJoinResult(result: number): void;
  OnRoomAddPlayer(player: string): void;
  OnRoomRemovePlayer(hint: number, player: string): void;
  OnRoomReceiveChat(player: string, chat: string): void;
  OnRoomAddGame(
    player: string,
    gameid: number,
    params: GameParams,
    minplayers: number,
    maxplayers: number,
    title: string,
    ctotal: number,
  ): void;
  OnRoomRemoveGame(gameid: number, ctotal: number): void;
  OnRoomGameInProgress(gameid: number): void;
  OnRoomGamePlayerNames(gameid: number, cnames: number, anames: string[]): void;
  OnRoomStatusComplete(): void;
  OnRoomCreateGameResult(gameid: number, result: number, packid: PackId): void;
  OnRoomCanJoinGameResult(result: number): void;
  OnRoomLeaveResult(): void;
  OnGameJoinResult(result: number): void;
  OnGameReceiveChat(player: string, chat: string): void;
  OnGameNetMessage(nm: GameInner): void;
  OnGameKilled(gameid: number): void;
  OnGameLeaveResult(result: number): void;
  OnError(error: string): void;
  OnClose(error: number): void;
}

/** mpshared/xpump.cpp — frame reassembly (ProcessRead/Peek) + per-frame decode
 *  and dispatch (Dispatch/XMsgFromBuffer/DispatchXMsg). Receive side only: the
 *  C++ send queue (ProcessWrite's partial-send bookkeeping) is the platform
 *  socket's job here (WebSocket.send never partial-writes). */
export class XPump {
  private buf = new Uint8Array(0);
  private notify: XPumpNotify;

  constructor(notify: XPumpNotify) {
    this.notify = notify;
  }

  /** xpump.cpp:117-130 ProcessRead — accumulate; then Dispatch every complete
   *  frame (xpump.cpp:141-165; see shape note 2). */
  push(chunk: Uint8Array): void {
    const merged = new Uint8Array(this.buf.length + chunk.length);
    merged.set(this.buf);
    merged.set(chunk, this.buf.length);
    this.buf = merged;
    // Peek (xpump.cpp:132-139): leading big-endian u16 size, full frame buffered?
    while (this.buf.length >= 2) {
      const size = (this.buf[0] << 8) | this.buf[1];
      if (size < 3 || this.buf.length < size) break;
      const frame = this.buf.slice(0, size);
      this.buf = this.buf.slice(size);
      this.dispatchFrame(frame);
    }
  }

  /** xpump.cpp:166-339 XMsgFromBuffer + 341-720 DispatchXMsg, over the golden
   *  protocol codec. Unknown id → OnError (the C++ default arm). */
  private dispatchFrame(frame: Uint8Array): void {
    const n = this.notify;
    const id = frame[2];

    // game-layer frames decode through netmessage.ts (0x54/0x55; 0x56 is
    // client→server only — reaching here is a protocol error like any unknown id)
    if (id === XMSG.GAMENETMESSAGE || id === XMSG.GAMEUPDATEEMPTY) {
      const g = decodeGame(frame);
      if (g.kind === 'gamenetmessage') {
        n.OnGameNetMessage(decodeInner(g.nmid, g.inner));
      } else if (g.kind === 'gameupdateempty') {
        // messages.cpp:304-339 — synthesize the zero-command ScUpdate
        n.OnGameNetMessage({
          nmid: NMID.ScUpdate,
          kind: 'scupdate',
          cUpdatesBlock: g.cUpdatesBlock,
          cUpdatesSync: g.cUpdatesSync,
          commands: [],
        });
      }
      return;
    }

    // ids xmsg.ts leaves 'raw' but the client receives (XMsg1-shaped or bespoke)
    switch (id) {
      case XMSG.SIGNOUTRESULT: // XMsg1 (messages.h:157)
        n.OnSignOutResult(u32At(frame, 3));
        return;
      case XMSG.LOBBYCANJOINROOMRESULT: // XMsg1 (messages.h:164)
        n.OnLobbyCanJoinRoomResult(u32At(frame, 3));
        return;
      case XMSG.ROOMGAMEINPROGRESS: // XMsg1 (messages.h:177)
        n.OnRoomGameInProgress(u32At(frame, 3));
        return;
      case XMSG.ROOMCANJOINGAMERESULT: // XMsg1 (messages.h:180)
        n.OnRoomCanJoinGameResult(u32At(frame, 3));
        return;
      case XMSG.GAMELEAVERESULT: // XMsg1 (messages.h:186)
        n.OnGameLeaveResult(u32At(frame, 3));
        return;
      case XMSG.PROTOCOLERROR: // XMsg1 (messages.h:150); wis sends it pre-close
        n.OnError(`protocol error ${u32At(frame, 3)}`);
        return;
      case XMSG.SHOWMESSAGE: {
        // messages.cpp:18-40
        const r = new ByteReader(frame.slice(3));
        const ipRedirect = r.readDword();
        const disconnect = r.readDword() !== 0;
        const cbMessage = r.readDword();
        const message = readSz(r.readBytes(cbMessage));
        n.OnShowMessage(message, ipRedirect, disconnect);
        return;
      }
      case XMSG.ROOMADDGAME: {
        // messages.cpp:401-427 wire order
        const r = new ByteReader(frame.slice(3));
        const ctotal = r.readDword();
        const gameid = r.readDword();
        const minplayers = r.readDword();
        const maxplayers = r.readDword();
        const player = r.readFixedString(kcbPlayerName);
        const title = r.readFixedString(40); // kcbLevelTitle
        const params = decodeGameParams(r);
        n.OnRoomAddGame(player, gameid, params, minplayers, maxplayers, title, ctotal);
        return;
      }
      case XMSG.ROOMGAMEPLAYERNAMES: {
        // messages.cpp:475-489
        const r = new ByteReader(frame.slice(3));
        const gameid = r.readDword();
        const cnames = r.readDword();
        const anames: string[] = [];
        for (let i = 0; i < cnames; i++) anames.push(r.readFixedString(kcbPlayerName));
        n.OnRoomGamePlayerNames(gameid, cnames, anames);
        return;
      }
      case XMSG.ROOMCREATEGAMERESULT: {
        // messages.cpp:139-160
        const r = new ByteReader(frame.slice(3));
        const gameid = r.readDword();
        const result = r.readDword();
        const packid: PackId = { id: r.readDword(), hash: r.readBytes(16) };
        n.OnRoomCreateGameResult(gameid, result, packid);
        return;
      }
    }

    // everything else has a typed xmsg.ts decode
    const m = decode(frame);
    switch (m.id) {
      case XMSG.HANDSHAKERESULT: // XMsg2: result, id
        if (m.kind === 'dword2') n.OnHandshakeResult(m.dw0, m.dw1);
        return;
      case XMSG.ECHO:
        n.OnEcho();
        return;
      case XMSG.LOGINRESULT:
        if (m.kind === 'dword1') n.OnLoginResult(m.dw0);
        return;
      case XMSG.LOBBYJOINRESULT:
        if (m.kind === 'dword1') n.OnLobbyJoinResult(m.dw0);
        return;
      case XMSG.LOBBYLEAVERESULT:
        if (m.kind === 'dword1') n.OnLobbyLeaveResult(m.dw0);
        return;
      case XMSG.LOBBYCREATEROOMRESULT: // XMsg2: result, roomid
        if (m.kind === 'dword2') n.OnLobbyCreateRoomResult(m.dw0, m.dw1);
        return;
      case XMSG.LOBBYLURKERCOUNT:
        if (m.kind === 'dword1') n.OnLobbyLurkerCount(m.dw0);
        return;
      case XMSG.LOBBYADDROOM:
        if (m.kind === 'lobbyaddroom') {
          n.OnLobbyAddRoom(m.room, m.roomid, m.priv !== 0, m.cPlayers, m.cGames);
        }
        return;
      case XMSG.LOBBYREMOVEROOM:
        if (m.kind === 'dword1') n.OnLobbyRemoveRoom(m.dw0);
        return;
      case XMSG.LOBBYUPDATEROOM: // XMsg3: roomid, cPlayers, cGames
        if (m.kind === 'dword3') n.OnLobbyUpdateRoom(m.dw0, m.dw1, m.dw2);
        return;
      case XMSG.LOBBYRECEIVECHAT: // XMsgS2: player, chat
        if (m.kind === 'strings') n.OnLobbyReceiveChat(m.strings[0] ?? '', m.strings[1] ?? '');
        return;
      case XMSG.ROOMJOINRESULT:
        if (m.kind === 'dword1') n.OnRoomJoinResult(m.dw0);
        return;
      case XMSG.ROOMADDPLAYER: // XMsgS1: player
        if (m.kind === 'strings') n.OnRoomAddPlayer(m.strings[0] ?? '');
        return;
      case XMSG.ROOMREMOVEPLAYER: // XMsgDS: hint, player
        if (m.kind === 'dword_string') n.OnRoomRemovePlayer(m.dw0, m.s0);
        return;
      case XMSG.ROOMRECEIVECHAT:
        if (m.kind === 'strings') n.OnRoomReceiveChat(m.strings[0] ?? '', m.strings[1] ?? '');
        return;
      case XMSG.ROOMREMOVEGAME: // XMsg2: gameid, ctotal
        if (m.kind === 'dword2') n.OnRoomRemoveGame(m.dw0, m.dw1);
        return;
      case XMSG.ROOMSTATUSCOMPLETE:
        n.OnRoomStatusComplete();
        return;
      case XMSG.ROOMLEAVERESULT:
        n.OnRoomLeaveResult();
        return;
      case XMSG.GAMEJOINRESULT:
        if (m.kind === 'dword1') n.OnGameJoinResult(m.dw0);
        return;
      case XMSG.GAMERECEIVECHAT:
        if (m.kind === 'strings') n.OnGameReceiveChat(m.strings[0] ?? '', m.strings[1] ?? '');
        return;
      case XMSG.GAMEKILLED:
        if (m.kind === 'dword1') n.OnGameKilled(m.dw0);
        return;
      default:
        // xpump.cpp:316-319 default arm — unknown id is a stream error
        n.OnError(`unknown xmsg id 0x${id.toString(16)}`);
    }
  }
}

const u32At = (frame: Uint8Array, off: number): number =>
  new DataView(frame.buffer, frame.byteOffset, frame.byteLength).getUint32(off);
const readSz = (bytes: Uint8Array): string => {
  let end = bytes.length;
  while (end > 0 && bytes[end - 1] === 0) end--;
  return new TextDecoder('latin1').decode(bytes.subarray(0, end));
};

// ── XTransport (game/xtransport.cpp) ─────────────────────────────────────────

// xtransport.h:52-70 — the connection state machine's states.
export const XTS = {
  INVALID: -1,
  CLOSED: 0,
  CONNECTING: 1,
  CONNECTED: 2,
  HANDSHAKING: 3,
  HANDSHAKEERROR: 4,
  HANDSHAKESERVERFULL: 5,
  HANDSHAKESUCCESS: 6,
  OPEN: 7,
  LOGGINGIN: 8,
  LOGINFAILED: 9,
  LOGGEDIN: 10,
  SIGNINGOUT: 11,
  SIGNOUTFAILED: 12,
  SIGNEDOUT: 13,
  JOINLOBBY: 14,
  JOINLOBBYFAILED: 15,
  JOINLOBBYSUCCESS: 16,
  LOBBY: 17,
  CREATINGROOM: 18,
  CREATEROOMERROR: 19,
  CREATEROOMSUCCESS: 20,
  CANJOINROOM: 21,
  JOININGROOM: 22,
  JOINROOMERROR: 23,
  JOINROOMSUCCESS: 24,
  ROOM: 25,
  CREATINGGAME: 26,
  CREATEGAMEERROR: 27,
  CREATEGAMESUCCESS: 28,
  CANJOINGAME: 29,
  LEAVINGROOM: 30,
  JOININGGAME: 31,
  JOINGAMEERROR: 32,
  JOINGAMESUCCESS: 33,
  GAME: 34,
  RESUMINGGAME: 35, // TS-only reconnect: sent CsResumeGame, awaiting ScResumeResult (→ GAME on ok, → LOGGEDIN on fail)
} as const;
export type XtsState = (typeof XTS)[keyof typeof XTS];
const XTS_NAME: Record<number, string> = Object.fromEntries(
  Object.entries(XTS).map(([k, v]) => [v, k]),
);

export interface XTransportOpts {
  connect: XConnector;
  /** gszDeviceId (Multiplayer.cpp) — sent in XMsgLogin. */
  deviceId?: string;
  /** HostGetPlatformString / "bot" under WI_IS_BOT (xtransport.cpp:284-288). */
  platform?: string;
  /** kdwClientID / kdwProtocolCurrent (xtransport.cpp:226). */
  clientId?: number;
  protocol?: number;
  /** WaitForStateChange timeout — C++ ctWait default 30*100 ticks = 30s
   *  (xtransport.h:73). */
  waitMs?: number;
  log?: (msg: string) => void;
}

export class XTransport implements XPumpNotify {
  // xtransport.cpp:14-25 ctor inits
  private state_: XtsState = XTS.CLOSED;
  private error_ = false;
  private id_ = 0;
  private roomidCreate_ = 0;
  private gameidCreate_ = 0;
  private packidCreate_: PackId = { id: 0, hash: new Uint8Array(16) };
  private resultLogin_ = knLoginResultFail;
  private resultSignOut_ = knSignOutResultSuccess;
  private resultLobbyJoin_ = knLobbyJoinResultFail;
  private resultRoomJoin_ = knRoomJoinResultFail;
  private resultRoomCreate_ = knLobbyCreateRoomResultFail;
  private resultGameJoin_ = knGameJoinResultFail;
  private resultGameCreate_ = knRoomCreateGameResultFail;
  private resumeOk_ = false; // TS-only reconnect: the ScResumeResult outcome (see ResumeGame/onResumeResult)

  // comm.cpp Transport() — the callback slots
  private m_ptcb: ITransportCallback | null = null;
  private m_plcb: ILobbyCallback | null = null;
  private m_prcb: IRoomCallback | null = null;
  private m_pgcb: IGameCallback | null = null;

  private readonly opts: XTransportOpts;
  private readonly xpump_ = new XPump(this);
  private sock: XSocket | null = null;
  private sockClosed = true;
  // WaitForStateChange wakers (xtransport.cpp:69-72 "If waiting ... wake up")
  private waiters: (() => void)[] = [];

  constructor(opts: XTransportOpts) {
    this.opts = opts;
  }

  state(): XtsState {
    return this.state_;
  }
  /** Endpoint id assigned by the handshake (OnHandshakeResult dw1). */
  endpointId(): number {
    return this.id_;
  }

  private log(msg: string): void {
    this.opts.log?.(msg);
  }

  // xtransport.cpp:64-73
  private SetState(state: XtsState): void {
    this.log(`From: ${XTS_NAME[this.state_]} To: ${XTS_NAME[state]}`);
    this.state_ = state;
    const w = this.waiters;
    this.waiters = [];
    for (const wake of w) wake();
  }

  // xtransport.cpp:75-88
  private CheckState(state0: XtsState, state1: XtsState = XTS.INVALID): boolean {
    if (state0 !== this.state_ && state1 !== this.state_) {
      this.log(
        `Error! Current: ${XTS_NAME[this.state_]} Expected: ${XTS_NAME[state0]}` +
          (state1 !== XTS.INVALID ? ` or ${XTS_NAME[state1]}` : ''),
      );
      return false;
    }
    return true;
  }

  // xtransport.cpp:193-220 — see shape note 1. Resolves true on a state change,
  // false on error_ or timeout.
  private WaitForStateChange(msWait?: number): Promise<boolean> {
    const ms = msWait ?? this.opts.waitMs ?? 30_000;
    const stateEntry = this.state_;
    if (this.error_) return Promise.resolve(false);
    if (this.state_ !== stateEntry) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      const t = setTimeout(() => {
        const i = this.waiters.indexOf(wake);
        if (i >= 0) this.waiters.splice(i, 1);
        resolve(false);
      }, ms);
      const wake = () => {
        clearTimeout(t);
        resolve(!this.error_);
      };
      this.waiters.push(wake);
    });
  }

  private send(frame: Uint8Array): boolean {
    if (this.sock === null || this.sockClosed) return false;
    this.sock.send(frame);
    return true;
  }

  // ── the socket notify arms (xtransport.cpp:940-970) ──────────────────────
  // xtransport.cpp:959-961 — the connect completion wakes the Open() wait
  private OnConnectEvent(): void {
    this.SetState(XTS.CONNECTED);
  }
  private readonly sockNotify: XSocketNotify = {
    OnConnectEvent: () => {
      this.OnConnectEvent();
    },
    OnBytes: (bytes) => {
      this.xpump_.push(bytes);
    }, // OnEvent → xpump_.Dispatch
    OnError: (error) => {
      this.OnError(error);
    }, // :940-944
    OnClose: () => {
      this.OnClose(0);
    }, // :946-957
  };

  // ── Transport verbs ───────────────────────────────────────────────────────

  // xtransport.cpp:90-182
  async Open(): Promise<number> {
    this.error_ = false;
    let sock: XSocket;
    try {
      sock = this.opts.connect(this.sockNotify);
    } catch {
      this.log('CreateSocket() failed');
      return knTransportOpenResultNoNetwork;
    }
    this.sock = sock;
    this.sockClosed = false;
    this.SetState(XTS.CONNECTING);

    this.log('Waiting to connect');
    if (!(await this.WaitForStateChange())) {
      this.log('Timed out waiting for connect');
    }
    if (this.state_ !== XTS.CONNECTED) {
      this.log('Could not connect, closing socket');
      this.closeSocket();
      this.SetState(XTS.CLOSED);
      return knTransportOpenResultNotResponding;
    }

    // Handshake (xtransport.cpp:222-229)
    if (!this.CheckState(XTS.CONNECTED)) return knTransportOpenResultCantConnect;
    this.send(B.handshake(this.opts.clientId ?? CLIENT_ID, this.opts.protocol ?? PROTOCOL_CURRENT));
    this.SetState(XTS.HANDSHAKING);
    if (this.error_) {
      this.log('Handshake error');
      this.closeSocket();
      this.SetState(XTS.CLOSED);
      return knTransportOpenResultCantConnect;
    }

    this.log('Waiting for handshake reply');
    if (!(await this.WaitForStateChange())) {
      this.log('Timed out waiting for handshake reply');
      this.closeSocket();
      this.SetState(XTS.CLOSED);
      return knTransportOpenResultNotResponding;
    }
    // (this.state_ as XtsState): an awaited receive callback (WaitForStateChange above) may
    // have moved state_ to HANDSHAKEERROR/SERVERFULL, but TS's flow analysis still holds the
    // CONNECTED literal it narrowed to at the earlier guard and would flag these as no-overlap
    // (TS2367). The cast re-widens to the declared union; type-only, comparison unchanged.
    if ((this.state_ as XtsState) === XTS.HANDSHAKEERROR) {
      this.closeSocket();
      this.SetState(XTS.CLOSED);
      return knTransportOpenResultProtocolMismatch;
    }
    if ((this.state_ as XtsState) === XTS.HANDSHAKESERVERFULL) {
      this.closeSocket();
      this.SetState(XTS.CLOSED);
      return knTransportOpenResultServerFull;
    }

    this.SetState(XTS.OPEN);
    this.log('XTransport successfully opened!');
    return knTransportOpenResultSuccess;
  }

  // xtransport.cpp:184-191
  Close(): void {
    this.SetState(XTS.CLOSED);
    this.closeSocket();
  }
  IsClosed(): boolean {
    return this.state_ === XTS.CLOSED;
  }

  private closeSocket(): void {
    this.sockClosed = true;
    this.sock?.close();
    this.sock = null;
  }

  // xtransport.cpp:274-305
  async Login(username: string, token: string): Promise<number> {
    this.log('Logging in...');
    if (this.error_ || this.sockClosed) return knLoginResultFail;
    if (!this.CheckState(XTS.OPEN)) return knLoginResultFail;

    this.SetState(XTS.LOGGINGIN);
    this.send(B.login(username, token, this.opts.deviceId ?? '', this.opts.platform ?? 'web'));

    if (!(await this.WaitForStateChange())) {
      this.log('Timed out waiting for Login');
      this.resultLogin_ = knLoginResultFail;
    }
    if (this.state_ !== XTS.LOGGEDIN) {
      this.SetState(XTS.OPEN);
      return this.resultLogin_;
    }
    return this.resultLogin_;
  }

  // xtransport.cpp:327-356
  async SignOut(): Promise<number> {
    this.log('Signing Out...');
    if (this.error_ || this.sockClosed) return knSignOutResultFail;
    if (!this.CheckState(XTS.LOGGEDIN)) return knSignOutResultFail;

    this.SetState(XTS.SIGNINGOUT);
    this.send(encodeXMsg0(XMSG.SIGNOUT));

    if (!(await this.WaitForStateChange())) {
      this.log('Timed out waiting for sign out');
      this.resultSignOut_ = knSignOutResultFail;
    }
    if (this.state_ !== XTS.SIGNEDOUT) {
      this.SetState(XTS.LOGGEDIN);
      return this.resultSignOut_;
    }
    this.SetState(XTS.OPEN);
    return this.resultSignOut_;
  }

  // xtransport.cpp:377-379
  GetAnonymousUsername(): string {
    return `anon${this.id_}`;
  }

  // xtransport.cpp:381-405
  async JoinLobby(plcb: ILobbyCallback | null): Promise<number> {
    if (this.error_ || this.sockClosed) return knLobbyJoinResultFail;
    if (!this.CheckState(XTS.LOGGEDIN)) return knLobbyJoinResultFail;

    this.send(B.lobbyJoin());
    this.SetState(XTS.JOINLOBBY);

    this.log('Waiting for LobbyJoinResult');
    if (!(await this.WaitForStateChange())) {
      this.log('Timed out waiting for LobbyJoinResult');
    }
    if (this.state_ !== XTS.JOINLOBBYSUCCESS) {
      this.SetState(XTS.LOGGEDIN);
      return this.resultLobbyJoin_;
    }
    this.SetState(XTS.LOBBY);
    this.m_plcb = plcb;
    return this.resultLobbyJoin_;
  }

  // xtransport.cpp:442-453
  LeaveLobby(): boolean {
    this.m_plcb = null;
    if (this.error_ || this.sockClosed) return false;
    if (!this.CheckState(XTS.LOBBY)) return false;
    this.send(encodeXMsg0(XMSG.LOBBYLEAVE));
    this.SetState(XTS.LOGGEDIN);
    return true;
  }

  // xtransport.cpp:458-488
  async CreateRoom(
    roomname: string,
    password: string,
  ): Promise<{ result: number; roomid: number }> {
    this.log(`CreateRoom: ${roomname}`);
    if (this.error_ || this.sockClosed) {
      return { result: knLobbyCreateRoomResultFail, roomid: 0 };
    }
    if (!this.CheckState(XTS.LOBBY)) return { result: knLobbyCreateRoomResultFail, roomid: 0 };

    this.SetState(XTS.CREATINGROOM);
    this.send(encodeStrings(XMSG.LOBBYCREATEROOM, [roomname, password]));

    if (!(await this.WaitForStateChange())) {
      this.log('Timed out waiting room create');
      this.resultRoomCreate_ = knLobbyCreateRoomResultFail;
    }
    if (this.state_ !== XTS.CREATEROOMSUCCESS) {
      this.SetState(XTS.LOBBY);
      return { result: this.resultRoomCreate_, roomid: 0 };
    }
    this.SetState(XTS.LOBBY);
    return { result: this.resultRoomCreate_, roomid: this.roomidCreate_ };
  }

  // xtransport.cpp:509-528
  async CanJoinRoom(roomid: number, password: string): Promise<number> {
    if (this.error_ || this.sockClosed) return knRoomJoinResultFail;
    if (!this.CheckState(XTS.LOBBY)) return knRoomJoinResultFail;

    this.SetState(XTS.CANJOINROOM);
    this.send(encodeDwordString(XMSG.LOBBYCANJOINROOM, roomid, password));

    if (!(await this.WaitForStateChange())) {
      this.log('Timed out can wait joining room');
      this.resultRoomJoin_ = knRoomJoinResultFail;
    }
    this.SetState(XTS.LOBBY);
    return this.resultRoomJoin_;
  }

  // xtransport.cpp:550-582
  async JoinRoom(roomid: number, password: string, prcb: IRoomCallback | null): Promise<number> {
    if (this.error_ || this.sockClosed) return knRoomJoinResultFail;
    if (!this.CheckState(XTS.LOGGEDIN)) return knRoomJoinResultFail;

    this.SetState(XTS.JOININGROOM);
    this.send(B.roomJoin(roomid, password));

    if (!(await this.WaitForStateChange())) {
      this.log('Timed out waiting room join');
      this.resultRoomJoin_ = knRoomJoinResultFail;
    }
    if (this.state_ !== XTS.JOINROOMSUCCESS) {
      this.SetState(XTS.LOGGEDIN);
      return this.resultRoomJoin_;
    }
    this.m_prcb = prcb;
    this.SetState(XTS.ROOM);
    return this.resultRoomJoin_;
  }

  // xtransport.cpp:614-628
  SendChat(chat: string): boolean {
    if (this.error_ || this.sockClosed) return false;
    if (this.state_ === XTS.ROOM) {
      this.send(B.roomSendChat(chat));
      return true;
    }
    if (this.state_ === XTS.GAME) {
      this.send(B.gameSendChat(chat));
      return true;
    }
    return false;
  }

  /** Ask the server to add a bot to THIS game (creator-only — the DO authenticates by the connection, no
   *  token). Sent over the game socket. Returns false if not currently in a game. */
  RequestAddBot(): boolean {
    if (this.error_ || this.sockClosed || this.state_ !== XTS.GAME) return false;
    this.send(B.requestAddBot());
    return true;
  }

  /** Ask the server to remove the bot occupying staging-roster slot `pid` from THIS game (creator-only —
   *  authenticated by the connection, no token). Sent over the game socket. Returns false if not in a game. */
  RemoveBot(pid: number): boolean {
    if (this.error_ || this.sockClosed || this.state_ !== XTS.GAME) return false;
    this.send(B.requestRemoveBot(pid));
    return true;
  }

  /** TS-only reconnect: on a FRESH transport that's re-opened + re-logged-in (state LOGGEDIN), ask the DO
   *  to re-bind us to the slot it's holding for our did in `gameId` and replay the blocks we missed since
   *  `lastBlock` (Sim.resumeBlockPoint()). Mirrors Login()'s await-the-reply shape: ScResumeResult(1)
   *  moves us to GAME (the catch-up ScUpdate frames then flow to the game callback), ScResumeResult(0) (or
   *  a timeout) leaves us at LOGGEDIN. Resolves whether the slot was re-bound. */
  async ResumeGame(gameId: number, lastBlock: number): Promise<boolean> {
    if (this.error_ || this.sockClosed) return false;
    if (!this.CheckState(XTS.LOGGEDIN)) return false;
    this.resumeOk_ = false;
    this.SetState(XTS.RESUMINGGAME);
    this.send(B.resumeGame(gameId, lastBlock));
    if (!(await this.WaitForStateChange())) {
      // timed out waiting for ScResumeResult
      if ((this.state_ as XtsState) === XTS.RESUMINGGAME) this.SetState(XTS.LOGGEDIN);
      return false;
    }
    return (this.state_ as XtsState) === XTS.GAME && this.resumeOk_;
  }

  // xtransport.cpp:670-689
  async CanJoinGame(gameid: number): Promise<number> {
    if (this.error_ || this.sockClosed) return knRoomJoinResultFail; // :672 (C++ returns the ROOM code here)
    if (!this.CheckState(XTS.ROOM)) return knGameJoinResultFail;

    this.SetState(XTS.CANJOINGAME);
    this.send(encodeXMsg1(XMSG.ROOMCANJOINGAME, gameid));

    if (!(await this.WaitForStateChange())) {
      this.log('Timed out can wait joining game');
      this.resultGameJoin_ = knGameJoinResultFail;
    }
    this.SetState(XTS.ROOM);
    return this.resultRoomJoin_; // xtransport.cpp:688 — returns resultRoomJoin_ (C++ as-is)
  }

  // xtransport.cpp:711-745
  async CreateGame(
    params: GameParams,
  ): Promise<{ result: number; gameid: number; packidUpgrade: PackId }> {
    const failed = {
      result: knRoomCreateGameResultFail,
      gameid: 0,
      packidUpgrade: this.packidCreate_,
    };
    if (this.error_ || this.sockClosed) {
      this.log('Error or closed');
      return failed;
    }
    if (!this.CheckState(XTS.ROOM)) return failed;
    const w = new ByteWriter();
    encodeGameParams(w, params);
    this.send(B.roomCreateGame(w.toUint8Array()));

    this.SetState(XTS.CREATINGGAME);
    if (!(await this.WaitForStateChange())) {
      this.log('timed out waiting for XMsgRoomCreateGameResult');
      this.SetState(XTS.ROOM);
      return failed;
    }
    if (!this.CheckState(XTS.CREATEGAMEERROR, XTS.CREATEGAMESUCCESS)) {
      this.log('expected CREATEGAMESUCCESS or ERROR state');
      return failed;
    }
    if (this.state_ === XTS.CREATEGAMEERROR) {
      this.SetState(XTS.ROOM);
      return { result: this.resultGameCreate_, gameid: 0, packidUpgrade: this.packidCreate_ };
    }
    this.SetState(XTS.ROOM);
    return {
      result: this.resultGameCreate_,
      gameid: this.gameidCreate_,
      packidUpgrade: this.packidCreate_,
    };
  }

  // xtransport.cpp:771-783
  async LeaveRoom(hint: number): Promise<void> {
    this.m_prcb = null;
    if (this.error_ || this.sockClosed) return;
    if (!this.CheckState(XTS.ROOM)) return;
    this.SetState(XTS.LEAVINGROOM);
    this.send(B.roomLeave(hint));
    await this.WaitForStateChange();
    this.SetState(XTS.LOGGEDIN);
  }

  // xtransport.cpp:795-828
  async JoinGame(gameid: number, roomid: number): Promise<number> {
    if (this.error_ || this.sockClosed) {
      this.log('Error or closed');
      return knGameJoinResultFail;
    }
    // Must be in XTS_LOGGEDIN state, not in room, lobby, or game.
    if (!this.CheckState(XTS.LOGGEDIN)) return knGameJoinResultFail;

    this.SetState(XTS.JOININGGAME);
    this.send(B.gameJoin(gameid, roomid));
    if (!(await this.WaitForStateChange())) {
      this.log('timed out waiting for XMsgGameJoinResult');
      this.SetState(XTS.LOGGEDIN);
      return knGameJoinResultFail;
    }
    if (!this.CheckState(XTS.JOINGAMEERROR, XTS.JOINGAMESUCCESS)) {
      this.log('expected JOINGAMESUCCESS or ERROR state');
      return knGameJoinResultFail;
    }
    if (this.state_ === XTS.JOINGAMEERROR) {
      this.SetState(XTS.LOGGEDIN);
      return this.resultGameJoin_;
    }
    this.SetState(XTS.GAME);
    return this.resultGameJoin_;
  }

  // xtransport.cpp:857-865 + messages.cpp:205-245 NetMessage::ToBuffer routing
  SendNetMessage(nm: GameInner): boolean {
    if (this.error_ || this.sockClosed) return false;
    if (!this.CheckState(XTS.GAME)) return false;
    if (nm.kind === 'updateresult') {
      // CsUpdateResult rides the optimized 0x56 frame (messages.cpp:214)
      return this.send(B.updateResult(nm.cUpdatesBlock, nm.hash, nm.cmsLatency));
    }
    return this.send(
      encodeGame({
        id: XMSG.GAMENETMESSAGE,
        kind: 'gamenetmessage',
        nmid: nm.nmid,
        inner: encodeInner(nm),
      }),
    );
  }

  // xtransport.cpp:890-920
  async LeaveGame(): Promise<void> {
    if (this.error_ || this.sockClosed) {
      this.log('Error or closed');
      return;
    }
    // Server may have killed the game already (state reverted to LOGGEDIN).
    if (this.state_ === XTS.LOGGEDIN) return;
    if (!this.CheckState(XTS.GAME)) {
      this.log('Not in game state!');
      return;
    }
    this.send(B.gameLeave());
    if (!(await this.WaitForStateChange())) {
      this.log('timed out waiting for disconnect game result');
    }
    this.SetState(XTS.LOGGEDIN);
  }

  // xtransport.cpp:972-983
  UpdateAllies(side: number, sidmAllies: number): void {
    if (this.error_ || this.sockClosed) return;
    if (this.state_ === XTS.ROOM) return;
    if (this.state_ === XTS.GAME) {
      this.send(encodeXMsg2(XMSG.GAMEUPDATEALLIES, side >>> 0, sidmAllies >>> 0));
    }
  }

  // xtransport.cpp:985-987
  DisconnectSharedAccounts(): void {
    this.send(encodeXMsg0(XMSG.DISCONNECTSHAREDACCOUNTS));
  }

  // comm.cpp:12-30
  SetCallback(ptcb: ITransportCallback | null): ITransportCallback | null {
    const old = this.m_ptcb;
    this.m_ptcb = ptcb;
    return old;
  }
  GetCallback(): ITransportCallback | null {
    return this.m_ptcb;
  }
  SetGameCallback(pgcb: IGameCallback | null): IGameCallback | null {
    const old = this.m_pgcb;
    this.m_pgcb = pgcb;
    return old;
  }
  GetGameCallback(): IGameCallback | null {
    return this.m_pgcb;
  }

  // ── XPumpNotify arms (the receive-side state machine) ─────────────────────

  // xtransport.cpp:231-252
  OnHandshakeResult(result: number, id: number): void {
    if (!this.CheckState(XTS.HANDSHAKING)) return;
    switch (result) {
      case knHandshakeResultSuccess:
        this.id_ = id;
        this.SetState(XTS.HANDSHAKESUCCESS);
        return;
      case knHandshakeResultServerFull:
        this.SetState(XTS.HANDSHAKESERVERFULL);
        return;
      default:
        this.SetState(XTS.HANDSHAKEERROR);
    }
  }

  // xtransport.cpp:254-268
  OnShowMessage(message: string, _ipRedirect: number, _disconnect: boolean): void {
    if (this.error_ || this.sockClosed) return;
    if (this.m_ptcb !== null && message.length !== 0) {
      this.m_ptcb.OnShowMessage(message);
    }
  }

  // xtransport.cpp:270-272
  OnEcho(): void {
    this.send(encodeXMsg0(XMSG.ECHO));
  }

  // xtransport.cpp:307-325
  OnLoginResult(resultLogin: number): void {
    if (!this.CheckState(XTS.LOGGINGIN)) {
      this.resultLogin_ = knLoginResultFail;
      return;
    }
    if (this.error_ || this.sockClosed) {
      this.resultLogin_ = knLoginResultFail;
      return;
    }
    this.resultLogin_ = resultLogin;
    if (resultLogin !== knLoginResultSuccess && resultLogin !== knLoginResultAnonymousSuccess) {
      this.SetState(XTS.LOGINFAILED);
      return;
    }
    this.SetState(XTS.LOGGEDIN);
  }

  // xtransport.cpp:358-375
  OnSignOutResult(result: number): void {
    if (!this.CheckState(XTS.SIGNINGOUT)) {
      this.resultSignOut_ = knSignOutResultFail;
      return;
    }
    if (this.error_ || this.sockClosed) {
      this.resultSignOut_ = knSignOutResultFail;
      return;
    }
    this.resultSignOut_ = result;
    if (result !== knSignOutResultSuccess) {
      this.SetState(XTS.SIGNOUTFAILED);
      return;
    }
    this.SetState(XTS.SIGNEDOUT);
  }

  // xtransport.cpp:407-421
  OnLobbyJoinResult(result: number): void {
    if (this.error_ || this.sockClosed) return;
    if (!this.CheckState(XTS.JOINLOBBY)) return;
    this.resultLobbyJoin_ = result;
    if (result === knLobbyJoinResultSuccess) {
      this.SetState(XTS.JOINLOBBYSUCCESS);
      return;
    }
    this.SetState(XTS.JOINLOBBYFAILED);
  }

  // xtransport.cpp:455-456 — empty in the C++
  OnLobbyLeaveResult(_result: number): void {}

  // xtransport.cpp:490-507
  OnLobbyCreateRoomResult(result: number, roomid: number): void {
    if (!this.CheckState(XTS.CREATINGROOM)) {
      this.resultRoomCreate_ = knLobbyCreateRoomResultFail;
      return;
    }
    if (this.error_ || this.sockClosed) {
      this.resultRoomCreate_ = knLobbyCreateRoomResultFail;
      return;
    }
    this.resultRoomCreate_ = result;
    this.roomidCreate_ = roomid;
    if (result !== knLobbyCreateRoomResultSuccess) {
      this.SetState(XTS.CREATEROOMERROR);
      return;
    }
    this.SetState(XTS.CREATEROOMSUCCESS);
  }

  // xtransport.cpp:530-536
  OnLobbyCanJoinRoomResult(result: number): void {
    if (!this.CheckState(XTS.CANJOINROOM)) return;
    this.resultRoomJoin_ = result;
    this.SetState(XTS.LOBBY);
  }

  // xtransport.cpp:538-548
  OnLobbyLurkerCount(count: number): void {
    this.m_plcb?.OnLurkerCount(count);
  }
  OnLobbyReceiveChat(player: string, chat: string): void {
    this.m_plcb?.OnReceiveChat(player, chat);
  }
  // xtransport.cpp:423-440
  OnLobbyAddRoom(
    room: string,
    roomid: number,
    priv: boolean,
    cPlayers: number,
    cGames: number,
  ): void {
    this.m_plcb?.OnAddRoom(room, roomid, priv, cPlayers, cGames);
  }
  OnLobbyRemoveRoom(roomid: number): void {
    this.m_plcb?.OnRemoveRoom(roomid);
  }
  OnLobbyUpdateRoom(roomid: number, cPlayers: number, cGames: number): void {
    this.m_plcb?.OnUpdateRoom(roomid, cPlayers, cGames);
  }

  // xtransport.cpp:584-600
  OnRoomJoinResult(result: number): void {
    if (!this.CheckState(XTS.JOININGROOM)) {
      this.resultRoomJoin_ = knRoomJoinResultFail;
      return;
    }
    if (this.error_ || this.sockClosed) {
      this.resultRoomJoin_ = knRoomJoinResultFail;
      return;
    }
    this.resultRoomJoin_ = result;
    if (result !== knRoomJoinResultSuccess) {
      this.SetState(XTS.JOINROOMERROR);
      return;
    }
    this.SetState(XTS.JOINROOMSUCCESS);
  }

  // xtransport.cpp:602-667
  OnRoomAddPlayer(player: string): void {
    this.m_prcb?.OnAddPlayer(player);
  }
  OnRoomRemovePlayer(hint: number, player: string): void {
    this.m_prcb?.OnRemovePlayer(hint, player);
  }
  OnRoomReceiveChat(player: string, chat: string): void {
    this.m_prcb?.OnReceiveChat(player, chat);
  }
  OnRoomAddGame(
    player: string,
    gameid: number,
    params: GameParams,
    minplayers: number,
    maxplayers: number,
    title: string,
    ctotal: number,
  ): void {
    this.m_prcb?.OnAddGame(player, gameid, params, minplayers, maxplayers, title, ctotal);
  }
  OnRoomRemoveGame(gameid: number, ctotal: number): void {
    this.m_prcb?.OnRemoveGame(gameid, ctotal);
  }
  OnRoomGameInProgress(gameid: number): void {
    this.m_prcb?.OnGameInProgress(gameid);
  }
  OnRoomGamePlayerNames(gameid: number, cnames: number, anames: string[]): void {
    this.m_prcb?.OnGamePlayerNames(gameid, cnames, anames);
  }
  OnRoomStatusComplete(): void {
    this.m_prcb?.OnStatusComplete();
  }

  // xtransport.cpp:691-697
  OnRoomCanJoinGameResult(result: number): void {
    if (!this.CheckState(XTS.CANJOINGAME)) return;
    this.resultGameJoin_ = result;
    this.SetState(XTS.ROOM);
  }

  // xtransport.cpp:747-769
  OnRoomCreateGameResult(gameid: number, result: number, packid: PackId): void {
    if (!this.CheckState(XTS.CREATINGGAME)) {
      this.resultGameCreate_ = knRoomCreateGameResultFail;
      return;
    }
    if (this.error_ || this.sockClosed) {
      this.resultGameCreate_ = knRoomCreateGameResultFail;
      return;
    }
    this.resultGameCreate_ = result;
    this.packidCreate_ = packid;
    this.gameidCreate_ = gameid;
    if (result !== knRoomCreateGameResultSuccess) {
      this.SetState(XTS.CREATEGAMEERROR);
      return;
    }
    this.SetState(XTS.CREATEGAMESUCCESS);
  }

  // xtransport.cpp:785-793
  OnRoomLeaveResult(): void {
    if (this.error_ || this.sockClosed) return;
    if (!this.CheckState(XTS.LEAVINGROOM)) return;
    this.SetState(XTS.LOGGEDIN);
  }

  // xtransport.cpp:830-849
  OnGameJoinResult(result: number): void {
    if (!this.CheckState(XTS.JOININGGAME)) return;
    if (this.error_ || this.sockClosed) {
      this.resultGameJoin_ = knGameJoinResultFail;
      return;
    }
    this.resultGameJoin_ = result;
    if (result !== knGameJoinResultSuccess) {
      this.log('Error connecting to game.');
      this.SetState(XTS.JOINGAMEERROR);
      return;
    }
    this.SetState(XTS.JOINGAMESUCCESS);
  }

  // xtransport.cpp:851-855
  OnGameReceiveChat(player: string, chat: string): void {
    this.m_pgcb?.OnReceiveChat(player, chat);
  }

  // xtransport.cpp:867-874
  OnGameNetMessage(nm: GameInner): void {
    // ScResumeResult (TS-only) is the reply to CsResumeGame — it arrives while we're RESUMINGGAME (not yet
    // GAME), so handle it BEFORE the GAME-state guard. ok=1 → GAME (the catch-up ScUpdate frames that
    // follow then flow through normally); ok=0 → back to LOGGEDIN. Either way it wakes the ResumeGame wait.
    if (nm.nmid === NMID.ScResumeResult) {
      this.resumeOk_ = nm.kind === 'rawinner' && nm.bytes.length >= 1 && nm.bytes[0] === 1;
      this.SetState(this.resumeOk_ ? XTS.GAME : XTS.LOGGEDIN);
      return;
    }
    if (!this.CheckState(XTS.GAME)) return;
    this.m_pgcb?.OnNetMessage(nm);
  }

  // xtransport.cpp:876-888
  OnGameKilled(_gameid: number): void {
    if (!this.CheckState(XTS.GAME)) return;
    // The server removed this client from the game.
    this.SetState(XTS.LOGGEDIN);
    this.m_pgcb?.OnGameDisconnect();
  }

  // xtransport.cpp:922-925
  OnGameLeaveResult(_result: number): void {
    this.SetState(XTS.LOGGEDIN);
  }

  // xtransport.cpp:940-944
  OnError(error: string): void {
    this.log(`socket error: ${error}`);
    this.error_ = true;
    this.OnClose(0);
  }

  // xtransport.cpp:946-957
  OnClose(error: number): void {
    this.log(`OnClose ${error}`);
    this.sockClosed = true;
    this.m_ptcb?.OnConnectionClose();
    this.SetState(XTS.CLOSED);
  }
}

// ── small senders build.ts doesn't cover (XMsg0/1/2/S* client→server) ────────
const encodeXMsg0 = (id: number): Uint8Array => encodeRaw(id, new ByteWriter());
const encodeXMsg1 = (id: number, dw0: number): Uint8Array => {
  const w = new ByteWriter();
  w.writeDword(dw0);
  return encodeRaw(id, w);
};
const encodeXMsg2 = (id: number, dw0: number, dw1: number): Uint8Array => {
  const w = new ByteWriter();
  w.writeDword(dw0);
  w.writeDword(dw1);
  return encodeRaw(id, w);
};
const encodeStrings = (id: number, strings: string[]): Uint8Array => {
  const w = new ByteWriter();
  for (const s of strings) {
    w.writeWord(s.length + 1);
    w.writeString(s, true);
  }
  return encodeRaw(id, w);
};
const encodeDwordString = (id: number, dw0: number, s0: string): Uint8Array => {
  const w = new ByteWriter();
  w.writeDword(dw0);
  w.writeWord(s0.length + 1);
  w.writeString(s0, true);
  return encodeRaw(id, w);
};
function encodeRaw(id: number, body: ByteWriter): Uint8Array {
  const out = new ByteWriter();
  out.writeWord(3 + body.length);
  out.writeByte(id);
  out.writeBytes(body.toUint8Array());
  return out.toUint8Array();
}
